class CollectionFilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = null;
    this.overlay = null;
    this.lastFocus = null;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-cfd-panel]');
    this.overlay = this.querySelector('[data-cfd-overlay]');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cfd-overlay]') || event.target.closest('[data-cfd-close]')) {
        this.close();
        return;
      }

      const tab = event.target.closest('[data-cfd-tab]');
      if (tab) {
        this.setTab(tab.dataset.cfdTab);
        return;
      }

      const viewResults = event.target.closest('[data-cfd-view-results]');
      if (viewResults) {
        event.preventDefault();
        this.applyFilters();
      }
    });

    this.addEventListener('change', (event) => {
      const selectAll = event.target.closest('[data-cfd-select-all]');
      if (selectAll) {
        const family = selectAll.closest('[data-cfd-family]');
        if (!family) return;
        family.querySelectorAll('input[data-cfd-shade]').forEach((input) => {
          if (!input.disabled) input.checked = selectAll.checked;
        });
        return;
      }

      if (event.target.matches('input[data-cfd-shade]')) {
        const family = event.target.closest('[data-cfd-family]');
        if (!family) return;
        const shades = [...family.querySelectorAll('input[data-cfd-shade]:not(:disabled)')];
        const selectAllInput = family.querySelector('[data-cfd-select-all]');
        if (selectAllInput) {
          selectAllInput.checked = shades.length > 0 && shades.every((el) => el.checked);
        }
      }
    });

    document.addEventListener('collection:filter-open', (event) => {
      this.open(event.detail || {});
    });
  }

  open({ tab = 'all', group = null } = {}) {
    this.lastFocus = document.activeElement;
    this.syncColorsFromUrl();
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('collection-filter-drawer-open');
    this.setTab(tab);

    if (group) {
      this.expandFamily(group);
    }

    document.addEventListener('keydown', this.onKeyDown);
    const focusTarget = this.querySelector('[data-cfd-close]') || this.panel;
    focusTarget?.focus();
  }

  /** Normalize a color option to a handle-like token (sage dream -> sage-dream). */
  static normalizeColorToken(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\+/g, ' ')
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /** Keep shade checkboxes in sync with URL color filters (hyphen/space/case insensitive). */
  syncColorsFromUrl() {
    const active = CollectionFilterDrawer.getActiveColorsFromUrl();

    this.querySelectorAll('input[data-cfd-shade]').forEach((input) => {
      const normalized = CollectionFilterDrawer.normalizeColorToken(input.value);
      input.checked = active.has(normalized);
    });

    this.querySelectorAll('[data-cfd-family]').forEach((family) => {
      const shades = [...family.querySelectorAll('input[data-cfd-shade]:not(:disabled)')];
      const selectAllInput = family.querySelector('[data-cfd-select-all]');
      if (selectAllInput) {
        selectAllInput.checked = shades.length > 0 && shades.every((el) => el.checked);
      }
    });

    CollectionFilterDrawer.syncColorBarSelection(active);
    CollectionFilterDrawer.syncColorChipsFromUrl(active);
  }

  /** Highlight color-family bar items that contain any active Color filter. */
  static syncColorBarSelection(activeSet) {
    let active = activeSet;
    if (!active) {
      active = CollectionFilterDrawer.getActiveColorsFromUrl();
    }

    document.querySelectorAll('[data-collection-color-bar] [data-family-shades]').forEach((btn) => {
      const shades = String(btn.getAttribute('data-family-shades') || '')
        .split(',')
        .map((s) => CollectionFilterDrawer.normalizeColorToken(s))
        .filter(Boolean);
      const selected = shades.some((shade) => active.has(shade));
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  static getActiveColorsFromUrl() {
    const active = new Set();
    const params = new URLSearchParams(window.location.search);
    params.getAll('filter.v.option.color').forEach((raw) => {
      if (!raw) return;
      let value = raw;
      try {
        value = decodeURIComponent(String(raw).replace(/\+/g, ' '));
      } catch (e) {
        value = String(raw).replace(/\+/g, ' ');
      }
      const normalized = CollectionFilterDrawer.normalizeColorToken(value);
      if (normalized) active.add(normalized);
    });
    return active;
  }

  static getActiveColorLabelsFromUrl() {
    const labels = [];
    const seen = new Set();
    const params = new URLSearchParams(window.location.search);
    params.getAll('filter.v.option.color').forEach((raw) => {
      if (!raw) return;
      let value = raw;
      try {
        value = decodeURIComponent(String(raw).replace(/\+/g, ' '));
      } catch (e) {
        value = String(raw).replace(/\+/g, ' ');
      }
      value = String(value).trim();
      const handle = CollectionFilterDrawer.normalizeColorToken(value);
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      labels.push({ label: value, handle });
    });
    return labels;
  }

  /** Ensure each URL color has a chip (fixes Sage Dream missing when active_values under-reports). */
  static syncColorChipsFromUrl(activeSet) {
    const form = document.getElementById('CollectionFacetFiltersForm');
    if (!form) return;

    const colors = CollectionFilterDrawer.getActiveColorLabelsFromUrl();
    const active = activeSet || new Set(colors.map((c) => c.handle));

    let chipsWrap = form.querySelector('.collection-filter-drawer__active-chips');
    let chipsRow = form.querySelector('[data-cfd-chips]') || form.querySelector('.collection-filter-drawer__chips');

    if (!colors.length) {
      // Remove only color chips; keep other filter chips if present
      if (chipsRow) {
        chipsRow.querySelectorAll('[data-color-chip]').forEach((chip) => {
          chip.closest('facet-remove')?.remove();
        });
        const hasOtherChips = [...chipsRow.querySelectorAll('.collection-filter-drawer__chip')].some(
          (chip) => !chip.classList.contains('collection-filter-drawer__clear-inline')
        );
        if (!hasOtherChips && chipsWrap) chipsWrap.remove();
      }
      CollectionFilterDrawer.syncFilterCountBadge();
      return;
    }

    if (!chipsWrap) {
      chipsWrap = document.createElement('div');
      chipsWrap.className = 'collection-filter-drawer__active-chips';
      chipsRow = document.createElement('div');
      chipsRow.className = 'collection-filter-drawer__chips';
      chipsRow.setAttribute('data-cfd-chips', '');
      chipsWrap.appendChild(chipsRow);
      const body = form.querySelector('.collection-filter-drawer__body');
      if (body) form.insertBefore(chipsWrap, body);
      else form.prepend(chipsWrap);
    } else if (!chipsRow) {
      chipsRow = document.createElement('div');
      chipsRow.className = 'collection-filter-drawer__chips';
      chipsRow.setAttribute('data-cfd-chips', '');
      chipsWrap.appendChild(chipsRow);
    }

    // Rebuild color chips from URL (source of truth)
    chipsRow.querySelectorAll('[data-color-chip]').forEach((chip) => {
      chip.closest('facet-remove')?.remove();
    });

    const clearHost =
      chipsRow.querySelector('.collection-filter-drawer__clear-inline')?.closest('facet-remove') || null;

    colors.forEach(({ label, handle }) => {
      const removeParams = new URLSearchParams(window.location.search);
      const kept = removeParams.getAll('filter.v.option.color').filter((raw) => {
        let value = raw;
        try {
          value = decodeURIComponent(String(raw).replace(/\+/g, ' '));
        } catch (e) {
          value = String(raw).replace(/\+/g, ' ');
        }
        return CollectionFilterDrawer.normalizeColorToken(value) !== handle;
      });
      removeParams.delete('filter.v.option.color');
      kept.forEach((v) => removeParams.append('filter.v.option.color', v));

      const qs = removeParams.toString();
      const href = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      const wrap = document.createElement('facet-remove');
      const link = document.createElement('a');
      link.href = href;
      link.className = 'collection-filter-drawer__chip';
      link.setAttribute('data-color-chip', '');
      link.setAttribute('data-color-handle', handle);
      link.appendChild(document.createTextNode(label + ' '));
      const x = document.createElement('span');
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';
      link.appendChild(x);
      wrap.appendChild(link);

      if (clearHost) chipsRow.insertBefore(wrap, clearHost);
      else chipsRow.appendChild(wrap);
    });

    if (!chipsRow.querySelector('.collection-filter-drawer__clear-inline')) {
      const clearWrap = document.createElement('facet-remove');
      const resultsUrl = form.dataset.resultsUrl || window.location.pathname;
      const clearLink = document.createElement('a');
      clearLink.href = resultsUrl;
      clearLink.className = 'collection-filter-drawer__clear-inline';
      clearLink.textContent = 'Clear All';
      clearWrap.appendChild(clearLink);
      chipsRow.appendChild(clearWrap);
    }

    CollectionFilterDrawer.syncFilterCountBadge();
  }

  static syncFilterCountBadge() {
    const params = new URLSearchParams(window.location.search);
    let count = 0;
    const colorHandles = new Set();
    params.getAll('filter.v.option.color').forEach((raw) => {
      const handle = CollectionFilterDrawer.normalizeColorToken(
        String(raw).replace(/\+/g, ' ')
      );
      if (handle) colorHandles.add(handle);
    });
    count += colorHandles.size;
    params.forEach((value, key) => {
      if (key === 'filter.v.option.color' || key === 'sort_by' || key === 'page' || key === 'section_id') return;
      if (key.startsWith('filter.')) count += 1;
    });

    document.querySelectorAll('[data-open-collection-filter][data-filter-tab="all"]').forEach((btn) => {
      const label = btn.querySelector('span');
      if (!label) return;
      const base = 'All Filters';
      label.textContent = count > 0 ? `${base} (${count})` : base;
    });
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('collection-filter-drawer-open');
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.close();
  }

  setTab(tabName) {
    const tabs = this.querySelectorAll('[data-cfd-tab]');
    if (!tabs.length) {
      this.querySelectorAll('[data-cfd-pane]').forEach((pane) => {
        pane.classList.toggle('is-active', pane.dataset.cfdPane === 'all');
      });
      return;
    }
    if (tabName === 'colors' && !this.querySelector('[data-cfd-tab="colors"]')) {
      tabName = 'all';
    }
    tabs.forEach((tab) => {
      const active = tab.dataset.cfdTab === tabName;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.querySelectorAll('[data-cfd-pane]').forEach((pane) => {
      pane.classList.toggle('is-active', pane.dataset.cfdPane === tabName);
    });
  }

  expandFamily(groupId) {
    let family;
    try {
      family = this.querySelector(`[data-cfd-family="${CSS.escape(groupId)}"]`);
    } catch (e) {
      family = this.querySelector(`[data-cfd-family="${groupId}"]`);
    }
    if (!family) return;

    this.querySelectorAll('[data-cfd-family]').forEach((el) => {
      if (el !== family) el.open = false;
    });
    family.open = true;
    requestAnimationFrame(() => {
      family.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  applyFilters() {
    const form = this.querySelector('form');
    if (!form || typeof FacetFiltersForm === 'undefined') return;

    const formData = new FormData(form);
    const toolbarSort = document.querySelector('#CollectionSortBy');
    if (toolbarSort && toolbarSort.value && !formData.has('sort_by')) {
      formData.set('sort_by', toolbarSort.value);
    }

    // Normalize color values to spaced form (Dusty Sage), keep repeated params — no hyphens, no dual-write.
    const colors = formData.getAll('filter.v.option.color');
    if (colors.length) {
      formData.delete('filter.v.option.color');
      const seen = new Set();
      colors.forEach((value) => {
        if (typeof value !== 'string') return;
        const spaced = value.includes('-') ? value.replace(/-/g, ' ') : value;
        if (seen.has(spaced)) return;
        seen.add(spaced);
        formData.append('filter.v.option.color', spaced);
      });
    }

    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, null, true);
    this.close();
  }
}

if (!customElements.get('collection-filter-drawer')) {
  customElements.define('collection-filter-drawer', CollectionFilterDrawer);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-collection-filter]');
  if (!trigger) return;
  event.preventDefault();
  document.dispatchEvent(
    new CustomEvent('collection:filter-open', {
      detail: {
        tab: trigger.dataset.filterTab || 'all',
        group: trigger.dataset.filterGroup || null,
      },
    })
  );
});

function syncColorBarWhenReady() {
  if (typeof CollectionFilterDrawer !== 'undefined') {
    if (CollectionFilterDrawer.syncColorBarSelection) {
      CollectionFilterDrawer.syncColorBarSelection();
    }
    if (CollectionFilterDrawer.syncColorChipsFromUrl) {
      CollectionFilterDrawer.syncColorChipsFromUrl();
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncColorBarWhenReady);
} else {
  syncColorBarWhenReady();
}

/* Custom Sort By dropdown (Birdy-style panel, not native select) */
function closeCollectionSort(root) {
  if (!root) return;
  const toggle = root.querySelector('[data-sort-toggle]');
  const menu = root.querySelector('.collection-sort__menu');
  if (!toggle || !menu) return;
  root.classList.remove('is-open');
  toggle.setAttribute('aria-expanded', 'false');
  menu.hidden = true;
}

function openCollectionSort(root) {
  if (!root) return;
  document.querySelectorAll('[data-collection-sort].is-open').forEach((el) => {
    if (el !== root) closeCollectionSort(el);
  });
  const toggle = root.querySelector('[data-sort-toggle]');
  const menu = root.querySelector('.collection-sort__menu');
  if (!toggle || !menu) return;
  root.classList.add('is-open');
  toggle.setAttribute('aria-expanded', 'true');
  menu.hidden = false;
}

document.addEventListener('click', (event) => {
  const sortRoot = event.target.closest('[data-collection-sort]');
  const toggle = event.target.closest('[data-sort-toggle]');
  const option = event.target.closest('[data-sort-option]');

  if (option && sortRoot) {
    event.preventDefault();
    const value = option.dataset.value;
    const select = sortRoot.querySelector('#CollectionSortBy');
    const label = sortRoot.querySelector('[data-sort-label]');
    if (!select || value == null) return;

    select.value = value;
    if (label) label.textContent = option.textContent.trim();

    sortRoot.querySelectorAll('[data-sort-option]').forEach((btn) => {
      const selected = btn === option;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    closeCollectionSort(sortRoot);

    // Only swap sort_by; keep filter query encoding exactly as-is (avoid + / %2C re-escape).
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(
        FacetFiltersForm.withSortBy(window.location.search.slice(1), value),
        null,
        true
      );
    } else {
      select.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return;
  }

  if (toggle && sortRoot) {
    event.preventDefault();
    if (sortRoot.classList.contains('is-open')) {
      closeCollectionSort(sortRoot);
    } else {
      openCollectionSort(sortRoot);
    }
    return;
  }

  document.querySelectorAll('[data-collection-sort].is-open').forEach((el) => closeCollectionSort(el));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-collection-sort].is-open').forEach((el) => closeCollectionSort(el));
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-collection-load-more]');
  if (!button) return;
  event.preventDefault();

  const url = button.dataset.nextUrl;
  if (!url || button.classList.contains('is-loading')) return;

  button.classList.add('is-loading');
  try {
    const sectionId = document.getElementById('product-grid')?.dataset.id;
    const fetchUrl = sectionId ? `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}` : url;
    const response = await fetch(fetchUrl);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const newItems = html.querySelectorAll('#product-grid > li');
    const grid = document.getElementById('product-grid');
    newItems.forEach((item) => grid?.appendChild(item));

    const nextButton = html.querySelector('[data-collection-load-more]');
    if (nextButton?.dataset.nextUrl) {
      button.dataset.nextUrl = nextButton.dataset.nextUrl;
    } else {
      button.closest('.collection-load-more-wrap')?.remove();
    }

    if (typeof currency === 'function') currency();
  } catch (error) {
    console.error('Load more failed', error);
  } finally {
    button.classList.remove('is-loading');
  }
});
